import { ActionIcon, Badge, Box, Button, Center, Flex, Loader, Modal, Select, Text, TextInput } from "@mantine/core";
import { IconTrash, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import trpc from "@/shared/utils/trpc";
import { successNotification } from "@/shared/utils/successNotification";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

export type SchemeItem = {
  scheme_bse_code: string;
  name: string;
  scheme_amc_name?: string | null;
};

type Props = {
  opened: boolean;
  specificTarget: string;
  onClose: () => void;
};

export function ViewCategoryModal({ opened, specificTarget, onClose }: Props) {
  const [allSchemes, setAllSchemes] = useState<SchemeItem[]>([]);
  const [initialCodes, setInitialCodes] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!opened || !specificTarget) return;

    const fetchSchemes = async () => {
      setLoading(true);
      try {
        const res = await trpc.schemes.listBySpecificTarget.query({ specificTarget });
        setAllSchemes(res.schemes as SchemeItem[]);
        setInitialCodes(res.currentBseCodes);
        setSelected(res.currentBseCodes);
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSchemes();
  }, [opened, specificTarget]);

  const schemeMap = useMemo(() => Object.fromEntries(allSchemes.map((s) => [s.scheme_bse_code, s])), [allSchemes]);

  const dropdownData = allSchemes
    .filter((s) => !selected.includes(s.scheme_bse_code))
    .map((s) => ({ value: s.scheme_bse_code, label: s.name }));

  const handleAdd = (value: string | null) => {
    if (!value) return;
    setSelected((prev) => [...prev, value]);
  };

  const handleRemove = (code: string) => {
    setSelected((prev) => prev.filter((c) => c !== code));
  };

  const isDirty = useMemo(() => {
    if (selected.length !== initialCodes.length) return true;
    const initialSet = new Set(initialCodes);
    return selected.some((code) => !initialSet.has(code));
  }, [selected, initialCodes]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await trpc.schemes.updateBySpecificTarget.mutate({
        specificTarget,
        schemeBseCodes: selected,
      });

      successNotification({
        title: "Category Updated",
        message: `Schemes for ${specificTarget} were updated successfully.`,
      });

      setInitialCodes(selected);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} fz="md">
          View Category
        </Text>
      }
      size="lg"
    >
      {loading ? (
        <Center py="xl">
          <Loader size="sm" type="dots" />
        </Center>
      ) : (
        <>
          <TextInput label="Fund category name" withAsterisk value={specificTarget} readOnly mb="md" />

          <Select
            label="Select Schemes under the category"
            withAsterisk
            placeholder="Select"
            data={dropdownData}
            value={null}
            onChange={handleAdd}
            searchable
            mb="sm"
          />

          {selected.length > 0 && (
            <Box mt="xs">
              <Flex wrap="wrap" gap="xs">
                {selected.map((code) => (
                  <Badge
                    key={code}
                    variant="outline"
                    color="slate"
                    size="lg"
                    radius="sm"
                    style={{ textTransform: "none", fontWeight: 400, maxWidth: "100%" }}
                    rightSection={
                      <ActionIcon size="xs" variant="transparent" color="slate" onClick={() => handleRemove(code)}>
                        <IconX size={10} />
                      </ActionIcon>
                    }
                  >
                    {schemeMap[code]?.name ?? code}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}
        </>
      )}

      <Flex justify="flex-end" gap="sm" mt="xl">
        <Button color="primary" onClick={handleUpdate} disabled={!isDirty || loading || updating} loading={updating}>
          Update
        </Button>
      </Flex>
    </Modal>
  );
}
